const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const SHORT_MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

interface CalendarDateParts {
  year: number;
  month: number;
  day: number;
}

function isValidDate({ year, month, day }: CalendarDateParts): boolean {
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

function parseCalendarDate(value: string): CalendarDateParts | null {
  const raw = value.trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/.exec(raw);
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(raw);

  const parts = iso
    ? { year: Number(iso[1]), month: Number(iso[2]), day: Number(iso[3]) }
    : us
      ? { year: Number(us[3]), month: Number(us[1]), day: Number(us[2]) }
      : null;

  return parts && isValidDate(parts) ? parts : null;
}

/**
 * "yyyy-MM-dd" for a native `<input type="date">`, from either that shape or the
 * "MM/dd/yyyy" the API renders. Empty for anything unparseable, which the input
 * shows as a blank field rather than rejecting.
 */
export function toIsoCalendarDate(value?: string): string {
  if (!value) return "";
  const parts = parseCalendarDate(value);
  if (!parts) return "";
  return `${parts.year}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
}

export function formatLongCalendarDate(value?: string): string {
  if (!value) return "—";
  const parts = parseCalendarDate(value);
  if (!parts) return value;
  return `${String(parts.day).padStart(2, "0")} ${MONTH_NAMES[parts.month - 1]}, ${parts.year}`;
}

/** The Monday starting the week that contains `date` (weeks run Mon–Sun). */
export function startOfWeek(date: Date): Date {
  const day = date.getDay();
  const distanceToMonday = day === 0 ? -6 : 1 - day;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + distanceToMonday);
}

/**
 * The Mon–Sun span containing `date`, collapsing whatever the two ends share:
 * "Jul 20 – 26, 2026" · "Jun 29 – Jul 5, 2026" · "Dec 28, 2026 – Jan 3, 2027".
 */
export function formatWeekRange(date: Date): string {
  const monday = startOfWeek(date);
  const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);

  const startMonth = SHORT_MONTH_NAMES[monday.getMonth()];
  const endMonth = SHORT_MONTH_NAMES[sunday.getMonth()];

  if (monday.getFullYear() !== sunday.getFullYear()) {
    return `${startMonth} ${monday.getDate()}, ${monday.getFullYear()} – ${endMonth} ${sunday.getDate()}, ${sunday.getFullYear()}`;
  }
  if (monday.getMonth() !== sunday.getMonth()) {
    return `${startMonth} ${monday.getDate()} – ${endMonth} ${sunday.getDate()}, ${sunday.getFullYear()}`;
  }
  return `${startMonth} ${monday.getDate()} – ${sunday.getDate()}, ${sunday.getFullYear()}`;
}

export function formatNumericCalendarDate(value?: string): string {
  if (!value) return "—";
  const parts = parseCalendarDate(value);
  if (!parts) return value;
  return `${String(parts.month).padStart(2, "0")}/${String(parts.day).padStart(2, "0")}/${parts.year}`;
}
